import React, { useEffect, useState } from 'react';
import { FilteredRolodexView } from "./FilteredRolodexView";
import { safeFetch, navigate } from "./App";
import { Heart, Briefcase, Calendar, Handshake, Users, XCircle, Plus, LogIn } from 'lucide-react';

export const SharedFavoritesList = ({ chatId }) => {
    const [listData, setListData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);

    const urlParams = new URLSearchParams(window.location.search);
    const listId = urlParams.get('list_id');
    const isGuest = chatId === 'guest';

    useEffect(() => {
        const fetchSharedList = async () => {
            if (!listId) {
                setError('לא נמצא מזהה רשימה');
                setLoading(false);
                return;
            }

            try {
                const res = await safeFetch(
                    `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/get-shared-list?list_id=${listId}`,
                    chatId || 'guest'
                );

                if (res.status === 404) {
                    setError('הרשימה לא נמצאה');
                    setLoading(false);
                    return;
                }

                const data = await res.json();
                console.log('📋 Fetched shared list:', data);
                setListData(data);
                setLoading(false);
            } catch (err) {
                console.error('❌ Failed to fetch shared list:', err);
                setError('שגיאה בטעינת הרשימה');
                setLoading(false);
            }
        };

        fetchSharedList();
    }, [listId, chatId]);

    const handleAddToMyLists = async () => {
        if (isGuest || !listData) return;

        setAdding(true);
        try {
            // Create a new favorites list with the same data
            const listName = `${listData.name} (מועתק)`;

            // Prepare the data object matching the expected format
            const newListData = {
                name: listName,
                businesses: listData.favourite_businesses?.map(b => b.business_id) || [],
                events: listData.favourite_businesses?.filter(b => b.type === 'event').map(b => b.business_id) || [],
                barters: [], // Add if the shared list includes barters
                groups: listData.favourite_groups?.map(g => JSON.parse(g.group_data)) || [],
                posts: listData.favourite_posts?.map(p => JSON.parse(p.post_data)) || [],
                zones: listData.favourite_zones || []
            };

            await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/create-user-favorites-list',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        data: newListData
                    }),
                }
            );

            setAdded(true);
            setTimeout(() => setAdded(false), 3000);
        } catch (err) {
            console.error('❌ Failed to add list to my lists:', err);
            alert('שגיאה בהוספת הרשימה');
        } finally {
            setAdding(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-surface via-white to-surface flex justify-center items-center">
                <div className="w-12 h-12 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-surface via-white to-surface flex justify-center items-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-line-subtle p-8 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-danger-50 to-danger-100/50 flex items-center justify-center">
                        <XCircle size={48} className="text-danger-500" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-bold text-ink-strong mb-2">{error}</h3>
                    <p className="text-sm text-ink-muted mb-6">הרשימה לא נמצאה או שאין לך הרשאה לצפות בה</p>
                    <button
                        onClick={() => window.history.back()}
                        className="px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                    >
                        חזור
                    </button>
                </div>
            </div>
        );
    }

    if (!listData) return null;

    const businesses = listData.favourite_businesses?.filter(b => b.type === 'business') || [];
    const events = listData.favourite_businesses?.filter(b => b.type === 'event') || [];
    const barters = []; // Add when backend supports it
    const groups = listData.favourite_groups || [];
    const totalCount = businesses.length + events.length + barters.length + groups.length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-surface via-white to-surface">
            <div className="flex justify-center px-4 sm:px-6">
                <div className="w-full max-w-5xl pb-20">
                    {/* Header Section */}
                    <div className="mx-4 sm:mx-8 mt-8 mb-8">
                        <div className="bg-white rounded-2xl shadow-lg border border-line-subtle p-6 sm:p-8">
                            <div className="flex items-start gap-6">
                                {/* Icon */}
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-danger-500 to-danger-600 flex items-center justify-center">
                                        <Heart size={32} className="text-white fill-white" strokeWidth={2} />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-2xl sm:text-3xl font-bold text-ink-strong mb-2">
                                        {listData.name || 'רשימת מועדפים'}
                                    </h1>
                                    <p className="text-sm text-ink-muted mb-4">
                                        רשימה משותפת • סה"כ {totalCount} פריטים
                                    </p>

                                    {/* Stats */}
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {businesses.length > 0 && (
                                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-500/10 text-secondary-700 text-xs font-semibold rounded-full border border-secondary-500/20">
                                                <Briefcase size={12} />
                                                {businesses.length} עסקים
                                            </span>
                                        )}
                                        {events.length > 0 && (
                                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-info-50 text-info-700 text-xs font-semibold rounded-full border border-info-100">
                                                <Calendar size={12} />
                                                {events.length} מפגשים
                                            </span>
                                        )}
                                        {barters.length > 0 && (
                                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-success-50 text-success-700 text-xs font-semibold rounded-full border border-success-100">
                                                <Handshake size={12} />
                                                {barters.length} ברטרים
                                            </span>
                                        )}
                                        {groups.length > 0 && (
                                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-brand-50 text-brand-700 text-xs font-semibold rounded-full border border-brand-200">
                                                <Users size={12} />
                                                {groups.length} קבוצות
                                            </span>
                                        )}
                                    </div>

                                    {/* Add to My Lists Button (only for registered users) */}
                                    {!isGuest && (
                                        <button
                                            onClick={handleAddToMyLists}
                                            disabled={adding || added}
                                            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${added
                                                    ? 'bg-success-500 text-white'
                                                    : 'bg-gradient-to-r from-secondary-500 to-secondary-700 text-white hover:shadow-lg'
                                                }`}
                                        >
                                            {adding ? (
                                                <>
                                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    מוסיף...
                                                </>
                                            ) : added ? (
                                                <>
                                                    <Heart size={20} className="fill-white" strokeWidth={2} />
                                                    נוסף לרשימות שלי!
                                                </>
                                            ) : (
                                                <>
                                                    <Plus size={20} strokeWidth={2.5} />
                                                    הוסף לרשימות שלי
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {/* Guest Message */}
                                    {isGuest && (
                                        <div className="bg-info-50 border border-info-100 rounded-xl p-4 flex items-center justify-between gap-4">
                                            <p className="text-sm text-info-700">
                                                <strong>התחבר כדי להוסיף רשימה זו לרשימות המועדפים שלך</strong>
                                            </p>
                                            <button
                                                onClick={() => navigate('home')}
                                                className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                                            >
                                                <LogIn size={18} strokeWidth={2.5} />
                                                התחבר
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="mx-4 sm:mx-8 space-y-8">
                        {/* Businesses */}
                        {businesses.length > 0 && (
                            <div>
                                <h2 className="text-xl font-bold text-ink-strong mb-4 flex items-center gap-2">
                                    <Briefcase size={24} className="text-secondary-500" />
                                    עסקים ({businesses.length})
                                </h2>
                                <FilteredRolodexView
                                    items={businesses}
                                    chatId={chatId || 'guest'}
                                    fromView='shared-favorites-list'
                                />
                            </div>
                        )}

                        {/* Events */}
                        {events.length > 0 && (
                            <div>
                                <h2 className="text-xl font-bold text-ink-strong mb-4 flex items-center gap-2">
                                    <Calendar size={24} className="text-info-600" />
                                    מפגשים ({events.length})
                                </h2>
                                <FilteredRolodexView
                                    items={events}
                                    chatId={chatId || 'guest'}
                                    fromView='shared-favorites-list'
                                />
                            </div>
                        )}

                        {/* Barters */}
                        {barters.length > 0 && (
                            <div>
                                <h2 className="text-xl font-bold text-ink-strong mb-4 flex items-center gap-2">
                                    <Handshake size={24} className="text-success-600" />
                                    ברטרים ({barters.length})
                                </h2>
                                <FilteredRolodexView
                                    items={barters}
                                    chatId={chatId || 'guest'}
                                    fromView='shared-favorites-list'
                                />
                            </div>
                        )}

                        {/* Groups */}
                        {groups.length > 0 && (
                            <div>
                                <h2 className="text-xl font-bold text-ink-strong mb-4 flex items-center gap-2">
                                    <Users size={24} className="text-brand-600" />
                                    קבוצות ({groups.length})
                                </h2>
                                <FilteredRolodexView
                                    items={groups.map(g => JSON.parse(g.group_data))}
                                    chatId={chatId || 'guest'}
                                    type="group"
                                    fromView='shared-favorites-list'
                                />
                            </div>
                        )}

                        {/* Empty State */}
                        {totalCount === 0 && (
                            <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                                <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-danger-50 to-danger-100/50 flex items-center justify-center">
                                    <Heart size={48} className="text-danger-500" strokeWidth={1.5} />
                                </div>
                                <h3 className="text-xl font-bold text-ink-strong mb-2">הרשימה ריקה</h3>
                                <p className="text-sm text-ink-muted">רשימה זו אינה מכילה פריטים כרגע</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
